import type * as React from "react";
import {
  CheckboxButton as AriaCheckboxButton,
  CheckboxField as AriaCheckboxField,
  type CheckboxFieldProps as AriaCheckboxFieldProps,
} from "react-aria-components";
import { Minus } from "../icons/line.tsx";
import { Check } from "../icons/solid.tsx";
import { base, checkbox, wrapper } from "./Checkbox.css.ts";
import { SupportingText } from "./shared/SupportingText.tsx";
import type { OmittedReactAriaProps } from "./types.ts";

// this is used make sure the checkbox is always the same size, regardless of `selected` state
const NoIcon: React.FC<{ className: string }> = ({ className }) => (
  <svg
    className={className}
    width={24}
    height={24}
    fill="none"
    viewBox="0 0 24 24"
  />
);

type ReactChildren = (string | number) | (string | number)[];

type Size = keyof typeof wrapper.classNames.variants.size;
type HitArea = keyof typeof wrapper.classNames.variants.hitArea;

interface BaseProps {
  /** Size of the checkbox. */
  size?: Size;
  /** Secondary text displayed below the label for additional context. */
  supportingText?: ReactChildren;
  /** Label text displayed next to the checkbox. */
  children?: ReactChildren;
  /** Expands the clickable label area beyond the visible box. Defaults to "default". */
  hitArea?: HitArea;
}

interface CheckboxProps
  extends BaseProps, Omit<AriaCheckboxFieldProps, OmittedReactAriaProps> {
  type?: "checkbox";
  /** Ref to the underlying input element. */
  inputRef?: AriaCheckboxFieldProps["inputRef"];
  /** Ref to the underlying label element. */
  ref?: React.Ref<HTMLLabelElement>;
}

export type Props = CheckboxProps;

const CheckboxIcon: React.FC<
  Pick<CheckboxProps, "isIndeterminate" | "isSelected"> & { size: Size }
> = ({ isSelected, isIndeterminate, size }) => {
  if (isIndeterminate === true) {
    return <Minus className={checkbox({ size })} />;
  }

  if (isSelected === true) {
    return <Check className={checkbox({ size })} />;
  }

  return <NoIcon className={checkbox({ size })} />;
};

/**
 * A checkbox input for binary choices. Supports labels, supporting text, and indeterminate state.
 */
export const Checkbox: React.FC<Props> = ({
  size = "m",
  hitArea,
  children,
  supportingText,
  ref,
  ...props
}) => {
  const { type: _type, ...propsWithoutType } = props;

  return (
    <AriaCheckboxField {...propsWithoutType}>
      {({ isIndeterminate, isSelected, isDisabled }) => (
        <AriaCheckboxButton ref={ref} className={wrapper({ size, hitArea })}>
          <div className={base({ size, round: false })}>
            <CheckboxIcon
              isSelected={isSelected}
              isIndeterminate={isIndeterminate}
              size={size}
            />
          </div>
          {children != null && (
            <SupportingText
              supportingText={supportingText}
              size={size}
              isDisabled={isDisabled}
            >
              {children}
            </SupportingText>
          )}
        </AriaCheckboxButton>
      )}
    </AriaCheckboxField>
  );
};
